import { useState } from "react";
import { Newspaper, Calendar, ChevronDown, ChevronUp, Lightbulb, ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";

interface Props {
  title: string;
  body: string;
  whyItMatters?: string | null;
  date?: string | null;
  sourceType?: string | null;
  prominent?: boolean;
}

const SOURCE_LABELS: Record<string, string> = {
  news: "News",
  case_study: "Case Study",
  data_point: "Data Point",
  trend: "Trend",
  incident: "Incident",
};

export default function CurrentExampleCard({ title, body, whyItMatters, date, sourceType, prominent = false }: Props) {
  const [expanded, setExpanded] = useState(prominent);

  const sourceLabel = SOURCE_LABELS[sourceType || "news"] || "Example";

  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-xl border p-4 transition-all",
        "border-[hsl(38_90%_60%/0.3)] bg-gradient-to-br from-[hsl(38_60%_18%/0.5)] via-card/55 to-[hsl(20_55%_16%/0.35)]",
        "shadow-[0_0_28px_-14px_hsl(38_95%_60%/0.55)]"
      )}
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[hsl(38_95%_65%/0.7)] to-transparent" />
      <div className="pointer-events-none absolute inset-y-0 left-0 w-[2px] bg-gradient-to-b from-[hsl(38_95%_65%/0.7)] via-[hsl(25_85%_60%/0.4)] to-transparent" />
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-center justify-between text-left"
      >
        <div className="flex items-center gap-2 min-w-0">
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[hsl(38_80%_50%/0.2)] ring-1 ring-[hsl(38_95%_65%/0.4)]">
            <Newspaper className="h-3.5 w-3.5 text-[hsl(38_95%_72%)]" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-[9px] font-heading font-semibold uppercase tracking-[0.18em] text-[hsl(38_95%_75%)]">
                Current Example
              </span>
              <span className="rounded-full bg-[hsl(38_70%_40%/0.22)] ring-1 ring-[hsl(38_85%_60%/0.35)] px-1.5 py-0.5 text-[8px] font-heading font-medium text-[hsl(38_85%_82%)]">
                {sourceLabel}
              </span>
            </div>
            <h4 className="font-heading text-sm font-semibold text-foreground truncate">{title}</h4>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 ml-2">
          {date && (
            <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
              <Calendar className="h-3 w-3" />
              <span>{new Date(date).toLocaleDateString("en-US", { month: "short", year: "numeric" })}</span>
            </div>
          )}
          {expanded ? <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />}
        </div>
      </button>

      {expanded && (
        <div className="mt-3 space-y-3">
          <p className="font-body text-sm leading-relaxed text-foreground/80">{body}</p>

          {whyItMatters && (
            <div className="flex gap-2 rounded-lg border border-[hsl(20_80%_60%/0.28)] bg-[hsl(20_65%_22%/0.35)] p-3">
              <Lightbulb className="h-3.5 w-3.5 shrink-0 text-[hsl(20_95%_72%)] mt-0.5" />
              <div>
                <p className="text-[10px] font-heading font-semibold uppercase tracking-[0.18em] text-[hsl(20_90%_78%)] mb-0.5">
                  Why It Matters
                </p>
                <p className="font-body text-xs leading-relaxed text-foreground/75">{whyItMatters}</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
